/* eslint-disable no-use-before-define */
import { DateTime } from "luxon";

export function toDateTimeNullable(
  date: undefined | null | string | Date | DateTime,
  tz?: string,
): null | DateTime {
  if (!date) return null;
  try {
    return toDateTime(date, tz);
  } catch {
    return null;
  }
}

export function toDateTime(
  date: string | Date | DateTime | undefined,
  tz?: string,
): DateTime {
  if (!date) throw new Error("No date was found");
  if (typeof date === "string") {
    // maybe guess format?
    const n = Number(date);
    if (n) {
      return DateTime.fromMillis(n);
    }
    const guess = DateTime.fromISO(date);
    if (guess?.isValid) return maybeSetZone(guess, tz);
    throw new Error(`Unable to parse date '${date}'`);
  } else if (typeof date === "number") {
    return DateTime.fromMillis(date);
  } else if (date instanceof Date) {
    return maybeSetZone(DateTime.fromJSDate(date), tz);
  }
  return maybeSetZone(date, tz);
}

export function toDateNullable(
  date: undefined | null | string | Date | DateTime,
  tz?: string,
): null | DateTime {
  if (!date) return null;

  if (typeof date === "string") {
    // maybe guess format?
    const guess = DateTime.fromISO(date);
    if (guess && guess.isValid) return maybeSetZone(guess, tz);
    return null;
  }
  if (date instanceof Date) {
    return maybeSetZone(DateTime.fromJSDate(date), tz);
  }
  return maybeSetZone(date, tz);
}

function maybeSetZone(date: DateTime, tz?: string | null) {
  if (tz) return date.setZone(tz);
  return date;
}

export function toJSDate(date: string | Date | DateTime) {
  const d = toDateTimeNullable(date);
  return d ? d.toJSDate() : null;
}

export function getISOString(date: string | Date | DateTime): string | null {
  const d = toDateTimeNullable(date);
  return d ? d.toISO() : null;
}

export function formatDateTime(
  date: undefined | null | string | Date | DateTime,
  tz?: string,
): string {
  const dt = toDateTimeNullable(date, tz);
  return dt ? dt.toFormat("dd/MM/yyyy t") : "";
}

export function toHumanDuration(duration: number) {
  const fields: string[] = [];

  let totalSeconds = duration;

  if (totalSeconds > 86400) {
    const days = Math.floor(totalSeconds / 86400);
    totalSeconds = days % 86400;
    fields.push(`${days} days`);
  }
  if (totalSeconds > 3600) {
    const hours = Math.floor(totalSeconds / 3600);
    totalSeconds %= 3600;
    fields.push(`${hours} hours`);
  }
  if (totalSeconds > 60) {
    const minutes = Math.floor(totalSeconds / 60);
    totalSeconds %= 60;
    fields.push(`${minutes} minutes`);
  }

  if (fields.length === 0) return "0 seconds";
  if (fields.length === 1) return fields[0];

  return `${fields.slice(0, fields.length - 1).join(", ")} and ${fields[fields.length - 1]
    }`;
}

/**
 * Adjusts the finish time when the start time changes, maintaining logical time relationships
 */
export function adjustFinishTimeForStartChange(
  newStart: DateTime,
  currentFinish: DateTime | null,
  tz?: string,
): DateTime {
  let newFinish = currentFinish || newStart.set({ hour: 23, minute: 0 });

  if (newStart > newFinish) {
    newFinish = newFinish.set({
      day: newStart.day,
      month: newStart.month,
      year: newStart.year,
    });
  }

  // if the date parts differ, align finish to start's date
  if (
    newStart.day !== newFinish.day ||
    newStart.month !== newFinish.month ||
    newStart.year !== newFinish.year
  ) {
    newFinish = newFinish.set({
      day: newStart.day,
      month: newStart.month,
      year: newStart.year,
    });
  }

  return newFinish;
}

/**
 * Adjusts the start time when the finish time changes, maintaining logical time relationships
 */
export function adjustStartTimeForFinishChange(
  newFinish: DateTime,
  currentStart: DateTime | null,
  tz?: string,
): DateTime {
  let newStart = currentStart || newFinish.set({ hour: 16, minute: 0 });

  if (newStart > newFinish) {
    let potentialNewStart = newFinish.set({
      day: newFinish.day,
      month: newFinish.month,
      year: newFinish.year,
    });
    if (potentialNewStart > newFinish) {
      potentialNewStart = potentialNewStart.startOf("day");
    }
    newStart = potentialNewStart;
  }

  return newStart;
}

/**
 * Parses a datetime-local input value into a DateTime, handling timezone conversion
 */
export function parseDateTimeLocalInput(
  value: string | null | undefined,
  tz?: string,
): DateTime | null {
  if (!value) return null;

  const parsed = tz
    ? DateTime.fromISO(value, { zone: tz, setZone: true })
    : DateTime.fromISO(value);

  return parsed && parsed.isValid ? parsed : toDateTimeNullable(value, tz);
}
